<template>
  <div>
    <l-map
      :zoom="zoom"
      :center="center"
      style="height: 500px; width: 100%; z-index:0;"
    >
      <l-control-layers ref="control" />

      <l-tile-layer
        :url="url"
        :attribution="attribution"
      />

      <station-marker :station="station" />

      <packet-layer-group
        name=" < 100 mW"
        :packets="filteredPacketsP1"
        :icon="satelliteIcon1"
      />

      <packet-layer-group
        name=" 101 to 500 mW"
        :packets="filteredPacketsP2"
        :icon="satelliteIcon2"
      />

      <packet-layer-group
        name=" 501 to 1000 mW"
        :packets="filteredPacketsP3"
        :icon="satelliteIcon3"
      />

      <packet-layer-group
        name="1001 to 2000 mW"
        :packets="filteredPacketsP4"
        :icon="satelliteIcon4"
      />

      <packet-layer-group
        name="> 2000 mW"
        :packets="filteredPacketsP5"
        :icon="satelliteIcon5"
      />

      <packet-layer-group
        name="CRC ERROR"
        :packets="filteredPacketsCRCerror"
        :icon="satelliteIconCRC"
      />

      <packet-layer-group
        name="No Telemetry"
        :packets="filteredPacketsNT"
        :icon="satelliteIconNT"
      />

      <heat-map
        v-if="statistics"
        :statistics="statistics"
      />

      <radiation-diagram
        v-if="station"
        :center="station.location"
      />

      <l-control-scale position="bottomleft" :imperial="true" :metric="true" />
    </l-map>
  </div>
</template>

<script setup>
/**
 * PacketMapGS.vue
 * Mapa de paquetes usando Vue 2.7 Composition API y vue2-leaflet.
 * - Filtra y agrupa paquetes por potencia y errores.
 * - Muestra estación, heatmap y diagrama de radiación.
 * - Usa composable para íconos de mapa.
 * - Mantiene compatibilidad con leaflet y subcomponentes.
 */

import {
  LMap,
  LTileLayer,
  LControlScale,
  LControlLayers
} from "vue2-leaflet";
import { useMapIcons } from "./map/composables/useMapIcons";
import StationMarker from "./map/StationMarker.vue";
import PacketLayerGroup from "./map/PacketLayerGroup.vue";
import HeatMap from "./map/HeatMap.vue";
import RadiationDiagram from "./map/RadiationDiagram.vue";
import { computed, ref, watch, onBeforeUnmount } from "vue";

// Props con tipado y validación
/**
 * @typedef {Object} PacketMapGSProps
 * @property {Array} packets - Lista de paquetes a mostrar en el mapa
 * @property {Object|null} station - Estación asociada
 * @property {Array} statistics - Estadísticas para heatmap
 */
/** @type {PacketMapGSProps} */
const props = defineProps({
  packets: {
    type: Array,
    default: () => []
  },
  station: {
    type: Object,
    default: null
  },
  statistics: {
    type: Array,
    default: () => []
  }
});

// Íconos del mapa (composable)
const {
  satelliteIconNT,
  satelliteIcon1,
  satelliteIcon2,
  satelliteIcon3,
  satelliteIcon4,
  satelliteIcon5,
  satelliteIconCRC
} = useMapIcons();

// Estado reactivo
const zoom = ref(3);
const center = ref(props.station ? props.station.location : [0, 0]);
const url = ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
const attribution = ref('&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors');

// Actualiza el centro si cambia la estación
watch(() => props.station, (newStation) => {
  if (newStation && newStation.location) {
    center.value = newStation.location;
  }
});

// Computed para filtrar paquetes por potencia y errores
const filteredPacketsP1 = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    !p.raw?.startsWith("RXJyb3JfQ1JD") &&
    p.txPower < 101
  ) ?? []
);

const filteredPacketsP2 = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    !p.raw?.startsWith("RXJyb3JfQ1JD") &&
    p.txPower > 100 &&
    p.txPower < 501
  ) ?? []
);

const filteredPacketsP3 = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    !p.raw?.startsWith("RXJyb3JfQ1JD") &&
    p.txPower > 500 &&
    p.txPower < 1001
  ) ?? []
);

const filteredPacketsP4 = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    !p.raw?.startsWith("RXJyb3JfQ1JD") &&
    p.txPower > 1000 &&
    p.txPower < 2001
  ) ?? []
);

const filteredPacketsP5 = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    !p.raw?.startsWith("RXJyb3JfQ1JD") &&
    p.txPower > 2001
  ) ?? []
);

const filteredPacketsNT = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    !p.raw?.startsWith("RXJyb3JfQ1JD") &&
    !p.txPower
  ) ?? []
);

const filteredPacketsCRCerror = computed(() =>
  props.packets?.filter(p =>
    typeof p.satPos !== 'undefined' &&
    p.raw?.startsWith("RXJyb3JfQ1JD")
  ) ?? []
);

// Limpieza de recursos del mapa (placeholder para futuras extensiones)
onBeforeUnmount(() => {
  // Aquí se puede limpiar listeners o recursos de leaflet si se añaden en el futuro
});
</script>
